<script setup>
  import { ref, watch, computed, defineEmits, inject, onMounted } from 'vue'
  import * as bootstrap from 'bootstrap'
  const emit = defineEmits(['modalButtonClick', 'closeButtonClicked'])
  const props = defineProps({
    modalType: {
      type: String,
      default: ''
    },
    modalTitle: {
      type: String,
      default: ''
    },
    modalButton: {
      type: String,
      default: ''
    },
    modalSize: {
      type: String,
      default: 'medium'
    },
    hideCloseButton: {
      type: Boolean,
      default: false
    },
    showFooter: {
      type: Boolean,
      default: false
    },
    noDefaultStyles: {
      type: Boolean,
      default: false
    },
    customClass: {
      type: String,
      default: ''
    },
    disableOutsideClick: {
      type: Boolean,
      default: false
    },
    elevated: {
      type: Boolean,
      default: false
    }
  })

  const modalRef = ref(null)
  let bootstrapModal = null
  let onModalHidden = null
  const emitter = inject('emitter')

  const removeStaleBackdrop = () => {
    if (document.querySelector('.modal.show')) {
      return
    }
    document.querySelectorAll('.modal-backdrop').forEach(el => el.remove())
    document.body.classList.remove('modal-open')
    document.body.style.removeProperty('overflow')
    document.body.style.removeProperty('padding-right')
  }

  const initializeModal = () => {
    if (modalRef.value && typeof bootstrap !== 'undefined') {
      if (onModalHidden) {
        modalRef.value.removeEventListener('hidden.bs.modal', onModalHidden)
      }
      onModalHidden = () => {
        emit('closeButtonClicked')
        removeStaleBackdrop()
      }
      bootstrapModal = new bootstrap.Modal(modalRef.value, {
        backdrop: props.disableOutsideClick ? 'static' : true,
        keyboard: !props.disableOutsideClick
      })
      modalRef.value.addEventListener('hidden.bs.modal', onModalHidden)
    }
  }

  onMounted(() => {
    emitter.on('closeBcsModal', () => {
      closeModal()
    })
  })

  const openModal = () => {
    if (bootstrapModal) {
      bootstrapModal.show()
    }
  }
  const clickButton = () => {
    emit('modalButtonClick')
  }
  const handleBtnClose = () => {
    emit('closeButtonClicked')
    closeModal()
  }
  const closeModal = () => {
    // Blur focused element inside modal to avoid aria-hidden focus issues
    const active = document.activeElement
    if (modalRef.value && active && modalRef.value.contains(active)) {
      active.blur()
    }
    if (bootstrapModal) {
      bootstrapModal.hide()
    }
  }
  const modalClasses = computed(() => {
    const baseClasses = {
      icon: 'bi icon-modal',
      title: 'alert-title text-center',
      button: 'btn'
    }
    switch (props.modalType) {
      case 'success':
        return {
          icon: `${baseClasses.icon} bi-check-circle text-success`,
          title: `${baseClasses.title} text-success`,
          button: `${baseClasses.button} btn-success`
        }
      case 'error':
        return {
          icon: `${baseClasses.icon} bi-exclamation-circle text-danger`,
          title: `${baseClasses.title} text-danger`,
          button: `${baseClasses.button} btn-danger`
        }
      case 'warning':
        return {
          icon: `${baseClasses.icon} bi-exclamation-triangle warning-text`,
          title: `${baseClasses.title} warning-text`,
          button: `${baseClasses.button} btn-warning`
        }
      case 'info':
        return {
          icon: `${baseClasses.icon} bi-info-circle text-primary`,
          title: `${baseClasses.title} info-text`,
          button: `${baseClasses.button} btn-primary`
        }
      case 'confirmation':
        return {
          title: `${baseClasses.title} info-text`,
          button: `${baseClasses.button} btn-primary`
        }
      default:
        return baseClasses
    }
  })
  defineExpose({ openModal, closeModal })
  watch(
    () => modalRef.value,
    (el) => {
      if (el) {
        initializeModal()
      }
    },
    { immediate: true }
  )

  watch(
    () => props.disableOutsideClick,
    (newVal, oldVal) => {
      if (oldVal === undefined || newVal === oldVal) {
        return
      }
      if (bootstrapModal) {
        bootstrapModal.dispose()
        bootstrapModal = null
      }
      initializeModal()
    }
  )
</script>
<template>
  <div
    class="modal fade mt-5"
    :class="{ 'bcs-modal-elevated': props.elevated }"
    ref="modalRef"
    tabindex="-1"
    role="dialog"
    aria-labelledby="BcsModal"
    aria-hidden="true">
    <div class="modal-dialog custom-modal-dialog" :class="[props.customClass, props.modalSize]" role="document">
      <div class="modal-content" :class="props.customClass">
        <div class="bg-gray-100" :class="{'modal-header': !props.noDefaultStyles, 'rounded-top': props.noDefaultStyles}" v-if="$slots.header">
          <slot name="header"></slot>
        </div>
        <div :class="{'modal-body': !props.noDefaultStyles}">
          <div class="d-flex justify-content-end" v-if="!props.hideCloseButton">
            <button type="button" class="close btn" @click="handleBtnClose" aria-label="Close">
              <i class="bi bi-x-lg" aria-hidden="true"></i>
            </button>
          </div>
          <div class="text-center">
            <slot name="body-header"></slot>
          </div>
          <div class="text-center">
            <i :class="modalClasses.icon"></i>
          </div>
          <div :class="modalClasses.title" v-if="props.modalTitle">
            <strong>{{ props.modalTitle }}</strong>
          </div>
          <div class="alert-content text-14 text-center">
            <slot></slot>
          </div>
          <div class="text-center" v-if="props.modalButton">
            <button @click="clickButton" type="button" :class="modalClasses.button">
              {{ props.modalButton }}
            </button>
          </div>
          <div class="text-center">
            <slot name="footer-body"></slot>
          </div>
        </div>
        <div v-if="showFooter" :class="{'modal-footer': !props.noDefaultStyles}">
          <slot name="footer"></slot>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
  .bcs-modal-elevated {
    z-index: 1000060;
  }
  .icon-modal {
    font-size: 70px;
  }
  .warning-text {
    color: var(--bs-warning);
  }
  .btn-warning {
    background-color: var(--bs-warning);
    color: #212529;
  }
  .alert-title {
    font-size: 22px;
  }
  .alert-content {
    margin-bottom: 21px;
    margin-top: 10px;
  }
  .small {
    max-width: 555px;
    border-radius: 10px;
  }
  .medium {
    max-width: 800px;
    border-radius: 10px;
  }
  .modal-body {
    padding: 0;
    padding-left: 18px;
    padding-right: 18px;
    margin-bottom: 30px;
  }
  .info-text {
    color: #2e0c59;
  }
  .modal-footer {
    border-top: 1px solid #dee2e6;
    padding: 1rem;
    display: flex;
    justify-content: flex-end;
  }
</style>